import { _TrueTypeReader } from './ttf-reader';
import { _PdfDictionary, _PdfName, Dictionary } from './../pdf-primitives';
import { _PdfFontMetrics } from './pdf-font-metrics';
import { _StandardWidthTable } from './pdf-font-metrics';
import { _FontDescriptorFlag } from '../enumerator';
import { _decode } from '../utils';
import { _PdfStream } from './../base-stream';
/**
 * Handles embedding and subsetting of unicode truetype fonts for PDF output.
 *
 * @private
 */
var _UnicodeTrueTypeFont = /** @class */ (function () {
    function _UnicodeTrueTypeFont(data, size) {
        /**
         * Stores the alphabet used to generate random subset font names.
         *
         * @private
         */
        this._nameString = 'ABCDEFGHIJKLMNOPQRSTUVWXYZ';
        /**
         * Indicates whether the embedded font program has been written.
         *
         * @private
         */
        this._isEmbedFont = false;
        /**
         * Prefix content for the generated CMap stream.
         *
         * @private
         */
        this._cmapPrefix = '/CIDInit /ProcSet findresource begin\n12 dict begin\nbegincmap' + '\r\n' + '/CIDSystemInfo << /Registry (Adobe)/Ordering (UCS)/Supplement 0>> def\n/CMapName ' + '/Adobe-Identity-UCS def\n/CMapType 2 def\n1 beginCodeSpacerange' + '\r\n';
        /**
         * Terminator for the CMap code space range section.
         *
         * @private
         */
        this._cmapEndCodeSpaceRange = 'endCodeSpacerange' + '\r\n';
        /**
         * Marker that begins a bf range block.
         *
         * @private
         */
        this._cmapBeginRange = 'beginbfrange' + '\r\n';
        /**
         * Marker that ends a bf range block.
         *
         * @private
         */
        this._cmapEndRange = 'endbfrange' + '\r\n';
        /**
         * Suffix content that finalizes the CMap stream.
         *
         * @private
         */
        this._cmapSuffix = 'endbfrange\nendcmap\nCMapName currentdict ' + '/CMap defineresource pop\nend end' + '\r\n';
        if (data === null || typeof data === 'undefined') {
            throw new Error('ArgumentNullException:base64String');
        }
        if (size !== null && typeof size !== 'undefined') {
            this._fontSize = size;
        }
        if (typeof data === 'string') {
            this._fontData = _decode(data);
        }
        else {
            this._fontData = data;
        }
        this._ttfReader = new _TrueTypeReader(this._fontData);
        this._ttfMetrics = this._ttfReader._metrics;
    }
    /**
     * Prepares internal dictionaries and streams before writing to the PDF.
     *
     * @private
     * @returns {void} nothing.
     */
    _UnicodeTrueTypeFont.prototype._beginSave = function () {
        this._descendantFontBeginSave();
        this._cmapBeginSave();
        this._fontDictionaryBeginSave();
        this._fontProgramBeginSave();
        if (this._fontDescriptor) {
            this._fontDescriptor.update(this._ttfMetrics._contains ? 'FontFile3' : 'FontFile2', this._fontProgram);
            this._fontDescriptor._updated = true;
            this._fontDescriptor._isFont = true;
        }
    };
    /**
     * Updates descendant font width information before saving.
     *
     * @private
     * @returns {void} nothing.
     */
    _UnicodeTrueTypeFont.prototype._descendantFontBeginSave = function () {
        if (this._usedChars !== null && typeof this._usedChars !== 'undefined' && this._usedChars._size() > 0) {
            var width = this._getDescendantWidth(); // eslint-disable-line
            if (width !== null) {
                this._descendantFont.set('W', width);
            }
        }
    };
    /**
     * Adds the to unicode mapping stream to the font dictionary when required.
     *
     * @private
     * @returns {void} nothing.
     */
    _UnicodeTrueTypeFont.prototype._fontDictionaryBeginSave = function () {
        if (this._usedChars !== null && typeof this._usedChars !== 'undefined' && this._usedChars._size() > 0) {
            if (this._fontDictionary && !this._fontDictionary.has('ToUnicode')) {
                this._fontDictionary.update('ToUnicode', this._cmap);
            }
        }
    };
    /**
     * Creates all internal dictionaries streams and metrics required for pdf embedding.
     *
     * @private
     * @returns {void} nothing.
     */
    _UnicodeTrueTypeFont.prototype._createInternals = function () {
        this._fontDictionary = new _PdfDictionary();
        this._descendantFont = new _PdfDictionary();
        this._metrics = new _PdfFontMetrics();
        this._ttfReader._createInternals();
        this._usedChars = null;
        var data = []; // eslint-disable-line
        this._fontProgram = new _PdfStream(data, new _PdfDictionary());
        this._cmap = new _PdfStream(data, new _PdfDictionary());
        this._ttfMetrics = this._ttfReader._metrics;
        this._initializeMetrics();
        this._subsetName = this._getFontName();
        this._createDescendantFont();
        this._createFontDictionary();
    };
    /**
     * Returns the root font dictionary used by the pdf writer.
     *
     * @private
     * @returns {_PdfDictionary} The generated font dictionary.
     */
    _UnicodeTrueTypeFont.prototype._getInternals = function () {
        return this._fontDictionary;
    };
    /**
     * Maps truetype metrics into pdf font metric structures.
     *
     * @private
     * @returns {void} nothing.
     */
    _UnicodeTrueTypeFont.prototype._initializeMetrics = function () {
        var ttfMetrics = this._ttfReader._metrics;
        this._metrics._name = ttfMetrics._fontFamily;
        this._metrics._postScriptName = ttfMetrics._postScriptName;
        this._metrics._widthTable = new _StandardWidthTable(ttfMetrics._widthTable);
        this._metrics._subScriptSizeFactor = ttfMetrics._subScriptSizeFactor;
        this._metrics._superscriptSizeFactor = ttfMetrics._superscriptSizeFactor;
        this._metrics._isBold = ttfMetrics._isBold;
    };
    /**
     * Generates a random subset prefix combined with the postscript font name.
     *
     * @private
     * @returns {string} A random 6 character subset prefix.
     */
    _UnicodeTrueTypeFont.prototype._getFontName = function () {
        var builder = '';
        for (var i = 0; i < 6; i++) {
            var index = Math.floor(Math.random() * (25 - 0 + 1)) + 0;
            builder += this._nameString[index];
        }
        builder += '+';
        builder += this._ttfReader._metrics._postScriptName;
        return builder.toString();
    };
    /**
     * Creates the cid descendant font dictionary including system info and descriptor.
     *
     * @private
     * @returns {void} nothing.
     */
    _UnicodeTrueTypeFont.prototype._createDescendantFont = function () {
        this._descendantFont = new _PdfDictionary();
        this._descendantFont._updated = true;
        this._descendantFont.set('Type', new _PdfName('Font'));
        this._descendantFont.set('Subtype', new _PdfName('CIDFontType2'));
        this._descendantFont.set('BaseFont', new _PdfName(this._subsetName));
        this._descendantFont.set('CIDToGIDMap', new _PdfName('Identity'));
        this._descendantFont.set('DW', 1000);
        this._fontDescriptor = this._createFontDescriptor();
        this._descendantFont.set('FontDescriptor', this._fontDescriptor);
        var systemInfo = this._createSystemInfo();
        this._descendantFont.set('CIDSystemInfo', systemInfo);
        this._descendantFont._isFont = true;
    };
    /**
     * Constructs the font descriptor containing bounding box style and metric fields.
     *
     * @private
     * @returns {_PdfDictionary} The font descriptor dictionary.
     */
    _UnicodeTrueTypeFont.prototype._createFontDescriptor = function () {
        var descriptor = new _PdfDictionary();
        var metrics = this._ttfReader._metrics;
        descriptor.set('Type', new _PdfName('FontDescriptor'));
        descriptor.set('FontName', new _PdfName(this._subsetName));
        descriptor.set('Flags', this._getDescriptorFlags());
        descriptor.set('FontBBox', this._getBoundBox());
        descriptor.set('MissingWidth', metrics._widthTable[32]);
        descriptor.set('StemV', metrics._stemV);
        descriptor.set('ItalicAngle', metrics._italicAngle);
        descriptor.set('CapHeight', metrics._capHeight);
        descriptor.set('Ascent', metrics._winAscent);
        descriptor.set('Descent', metrics._winDescent);
        descriptor.set('Leading', metrics._leading);
        descriptor.set('AvgWidth', metrics._widthTable[32]);
        descriptor.set('MaxWidth', metrics._widthTable[32]);
        descriptor.set('XHeight', 0);
        descriptor.set('StemH', 0);
        descriptor._updated = true;
        return descriptor;
    };
    /**
     * Builds the embedded or subsetted font program using truetype reader output.
     *
     * @private
     * @returns {void} nothing.
     */
    _UnicodeTrueTypeFont.prototype._generateFontProgram = function () {
        var fontProgram = [];
        this._usedChars = (this._usedChars === null || typeof this._usedChars === 'undefined') ? new Dictionary()
            : this._usedChars;
        this._ttfReader._setOffset(0);
        if (this._ttfReader._isOpenType && this._ttfMetrics._contains) {
            fontProgram = this._ttfReader._readCompactFontFormatTable();
            this._fontProgram.dictionary.update('Subtype', _PdfName.get('CIDFontType0C'));
        }
        else {
            fontProgram = this._ttfReader._readFontProgram(this._usedChars);
        }
        this._fontProgram._clearStream();
        this._fontProgram._writeBytes(fontProgram);
    };
    /**
     * Returns the bounding box transformed into pdf descriptor format.
     *
     * @private
     * @returns {number[]} Bounding box array `[xMin, yMin, width, height]`.
     */
    _UnicodeTrueTypeFont.prototype._getBoundBox = function () {
        var rect = this._ttfReader._metrics._fontBox;
        var width = Math.abs(rect[2] - rect[0]);
        var height = Math.abs(rect[1] - rect[3]);
        var rectangle = [rect[0], rect[3], width, height];
        return rectangle;
    };
    /**
     * Triggers cmap generation prior to saving the font.
     *
     * @private
     * @returns {void} nothing.
     */
    _UnicodeTrueTypeFont.prototype._cmapBeginSave = function () {
        this._generateCmap();
    };
    /**
     * Prepares the font program stream at the start of the save process.
     *
     * @private
     * @returns {void} nothing.
     */
    _UnicodeTrueTypeFont.prototype._fontProgramBeginSave = function () {
        this._generateFontProgram();
    };
    /**
     * Converts a number into a hexadecimal string formatted for CMap entries.
     *
     * @private
     * @param {number} n - Number to convert.
     * @param {boolean} isCaseChange - Whether to uppercase the hex string.
     * @returns {string} Hexadecimal string wrapped in `<>`.
     */
    _UnicodeTrueTypeFont.prototype._toHexString = function (n, isCaseChange) {
        var s = n.toString(16);
        if (isCaseChange) {
            s = s.toUpperCase();
        }
        return '<0000'.substring(0, 5 - s.length) + s + '>';
    };
    /**
     * Builds the to unicode cmap stream using subset glyph mappings.
     *
     * @private
     * @returns {void} nothing.
     */
    _UnicodeTrueTypeFont.prototype._generateCmap = function () {
        var _this = this;
        if (this._usedChars !== null && typeof this._usedChars !== 'undefined' && this._usedChars._size() > 0) {
            var glyphChars_1 = this._ttfReader._getGlyphChars(this._usedChars);
            if (glyphChars_1._size() > 0) {
                var keys_1 = glyphChars_1.keys().sort();
                var first = keys_1[0];
                var last = keys_1[keys_1.length - 1];
                var middlePart = this._toHexString(first, false) + this._toHexString(last, false) + '\r\n';
                var builder_1 = '';
                builder_1 += this._cmapPrefix;
                builder_1 += middlePart;
                builder_1 += this._cmapEndCodeSpaceRange;
                var nextRange_1 = 0;
                keys_1.forEach(function (key, index) {
                    if (nextRange_1 === 0) {
                        if (index !== 0) {
                            builder_1 += _this._cmapEndRange;
                        }
                        nextRange_1 = Math.min(100, keys_1.length - index);
                        builder_1 += nextRange_1;
                        builder_1 += ' ';
                        builder_1 += _this._cmapBeginRange;
                    }
                    nextRange_1 -= 1;
                    builder_1 += _this._toHexString(key, true) + _this._toHexString(key, true);
                    builder_1 += _this._toHexString(glyphChars_1.getValue(key), true) + '\n';
                });
                builder_1 += this._cmapSuffix;
                this._cmap._clearStream();
                this._cmap._write(builder_1);
            }
        }
    };
    /**
     * Creates the top level type zero font dictionary used in the pdf.
     *
     * @private
     * @returns {void} nothing.
     */
    _UnicodeTrueTypeFont.prototype._createFontDictionary = function () {
        this._fontDictionary._updated = true;
        this._fontDictionary.set('Type', _PdfName.get('Font'));
        this._fontDictionary.set('Subtype', _PdfName.get('Type0'));
        this._fontDictionary.set('BaseFont', new _PdfName(this._subsetName));
        this._fontDictionary.set('Encoding', _PdfName.get('Identity-H'));
        this._fontDictionary.set('DescendantFonts', this._descendantFont);
        this._fontDictionary._isFont = true;
        this._fontDictionary._currentObj = this;
    };
    /**
     * Creates the cid system information dictionary for the descendant font.
     *
     * @private
     * @returns {void} nothing.
     */
    _UnicodeTrueTypeFont.prototype._createSystemInfo = function () {
        var systemInfo = new _PdfDictionary();
        systemInfo._updated = true;
        systemInfo.set('Registry', 'Adobe');
        systemInfo.set('Ordering', 'Identity');
        systemInfo.set('Supplement', 0);
        return systemInfo;
    };
    /**
     * Computes the font descriptor flags based on truetype style metrics.
     *
     * @private
     * @returns {number} The descriptor flag value.
     */
    _UnicodeTrueTypeFont.prototype._getDescriptorFlags = function () {
        var flags = 0;
        var metrics = this._ttfReader._metrics;
        if (metrics._isFixedPitch) {
            flags |= _FontDescriptorFlag.fixedPitch;
        }
        if (metrics._isSymbol) {
            flags |= _FontDescriptorFlag.symbolic;
        }
        else {
            flags |= _FontDescriptorFlag.nonSymbolic;
        }
        if (metrics._isItalic) {
            flags |= _FontDescriptorFlag.italic;
        }
        if (metrics._isBold) {
            flags |= _FontDescriptorFlag.forceBold;
        }
        return flags;
    };
    /**
     * Returns the width of a character based on truetype glyph metrics.
     *
     * @private
     * @param {string} charCode to get the width.
     * @returns {number} The character width.
     */
    _UnicodeTrueTypeFont.prototype._getCharacterWidth = function (charCode) {
        var codeWidth = this._ttfReader._getCharacterWidth(charCode);
        return codeWidth;
    };
    /**
     * Registers characters for inclusion in the subsetted font program.
     *
     * @private
     * @param {string} text to set the symbols.
     * @returns {void} nothing.
     */
    _UnicodeTrueTypeFont.prototype._setSymbols = function (text) {
        var _this = this;
        if (text !== null && typeof text !== 'undefined') {
            if (this._usedChars === null || typeof this._usedChars === 'undefined') {
                this._usedChars = new Dictionary();
            }
            text.split('').forEach(function (ch) {
                _this._usedChars.setValue(ch, String.fromCharCode(0));
            });
        }
    };
    /**
     * Generates the cid width array for the descendant font using glyph metrics.
     *
     * @private
     * @returns {Array<any>} The width array structure.
     */
    _UnicodeTrueTypeFont.prototype._getDescendantWidth = function () {
        var _this = this;
        var array = new Array(); // eslint-disable-line
        if (this._usedChars !== null && typeof this._usedChars !== 'undefined' && this._usedChars._size() > 0) {
            var glyphInfo_1 = [];
            var keys = this._usedChars.keys();
            keys.forEach(function (chLen) {
                var glyph = _this._ttfReader._getGlyph(chLen);
                glyphInfo_1.push(glyph);
            });
            glyphInfo_1.sort(function (a, b) { return a._index - b._index; });
            var firstGlyphIndex_1 = 0;
            var lastGlyphIndex_1 = 0;
            var firstGlyphIndexWasSet_1 = false;
            var widthDetails_1 = new Array(); // eslint-disable-line
            glyphInfo_1.forEach(function (glyph, index) {
                if (!firstGlyphIndexWasSet_1) {
                    firstGlyphIndexWasSet_1 = true;
                    firstGlyphIndex_1 = glyph._index;
                    lastGlyphIndex_1 = glyph._index - 1;
                }
                if ((lastGlyphIndex_1 + 1 !== glyph._index || (index + 1 === glyphInfo_1.length)) && glyphInfo_1.length > 1) {
                    array.push(Number(firstGlyphIndex_1));
                    if (index !== 0) {
                        array.push(widthDetails_1);
                    }
                    firstGlyphIndex_1 = glyph._index;
                    widthDetails_1 = new Array(); // eslint-disable-line
                }
                widthDetails_1.push(Number(glyph._width));
                if ((index + 1) === glyphInfo_1.length) {
                    array.push(Number(firstGlyphIndex_1));
                    array.push(widthDetails_1);
                }
                lastGlyphIndex_1 = glyph._index;
            });
        }
        return array;
    };
    return _UnicodeTrueTypeFont;
}());
export { _UnicodeTrueTypeFont };
